import { useUser } from '@clerk/clerk-react';
import { trackRender } from '../utils/debugRender';

export default function Settings() {
  const { user } = useUser();

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('Settings', { user });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">Admin configuration and preferences</p>
      </div>

      <div className="space-y-6">
        {/* Admin Profile */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Admin Profile</h2>
          <div className="flex items-center gap-4 mb-6">
            <img
              src={user?.imageUrl || '/avatar-placeholder.png'}
              alt="Admin"
              className="h-20 w-20 rounded-full"
            />
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-gray-600">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
              <p className="text-xs text-gray-500 mt-1">Admin User</p>
            </div>
          </div>
        </div>

        {/* Environment Info */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Environment</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">API URL</dt>
              <dd className="text-sm font-mono text-gray-900">
                {import.meta.env.VITE_WORKER_URL}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">Environment</dt>
              <dd className="text-sm font-mono text-gray-900">
                {import.meta.env.MODE}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">Version</dt>
              <dd className="text-sm font-mono text-gray-900">1.0.0</dd>
            </div>
          </dl>
        </div>

        {/* Quick Actions */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <a
              href="https://dashboard.clerk.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
            >
              <span className="text-2xl">🔐</span>
              <div>
                <p className="font-medium text-gray-900">Clerk Dashboard</p>
                <p className="text-sm text-gray-600">Manage authentication</p>
              </div>
            </a>
            <a
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
            >
              <span className="text-2xl">💳</span>
              <div>
                <p className="font-medium text-gray-900">Stripe Dashboard</p>
                <p className="text-sm text-gray-600">Manage subscriptions</p>
              </div>
            </a>
            <a
              href="https://pb.peetrack.live"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
            >
              <span className="text-2xl">💾</span>
              <div>
                <p className="font-medium text-gray-900">PostgREST</p>
                <p className="text-sm text-gray-600">Database API</p>
              </div>
            </a>
            <a
              href="https://peetrack.live"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 hover:bg-gray-50"
            >
              <span className="text-2xl">🌐</span>
              <div>
                <p className="font-medium text-gray-900">PeeTrack App</p>
                <p className="text-sm text-gray-600">Main application</p>
              </div>
            </a>
          </div>
        </div>

        {/* Admin Users */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Admin Users</h2>
          <p className="text-sm text-gray-600 mb-4">
            Manage admin access and permissions
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <img
                  src={user?.imageUrl || '/avatar-placeholder.png'}
                  alt="Admin"
                  className="h-10 w-10 rounded-full"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
              </div>
              <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                Active
              </span>
            </div>
          </div>
          <button className="mt-4 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Manage Admin Users in Clerk Dashboard
          </button>
        </div>

        {/* Audit Log */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {[
              { action: 'User upgraded', user: 'user_123', time: '2 hours ago' },
              { action: 'Error reviewed', user: 'admin', time: '5 hours ago' },
              { action: 'Dashboard accessed', user: user?.firstName, time: 'Just now' }
            ].map((log, index) => (
              <div
                key={index}
                className="flex items-start gap-3 rounded-lg bg-gray-50 p-3"
              >
                <div className="h-2 w-2 rounded-full bg-primary mt-1.5"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{log.action}</p>
                  <p className="text-xs text-gray-500">
                    by {log.user} • {log.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <button className="mt-4 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            View Full Audit Log
          </button>
        </div>

        {/* Documentation */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Documentation</h2>
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-600">
              For detailed documentation and setup instructions, see the README.md file in the
              repository.
            </p>
            <ul className="mt-3 text-sm text-gray-600 space-y-1">
              <li>Database migrations in <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">/migration</code></li>
              <li>API documentation in README.md</li>
              <li>Environment configuration in <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">.env.example</code></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
